import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Fade in lazy loaded images with React and CSS – a quick guide",
  "description": "",
  "published": "2018-01-31T00:00:00.000Z",
  "redirect_from": ["/blog/fade-in-lazy-loaded-images-react-css-quick-guide/swizec/8163"],
  "categories": "Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Say you want to lazy load some images on your website. You don't want them to just pop into existence and scare the user. A nice fade in effect works much better.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/01/gif.gif",
        "alt": null
      }}></img></p>
    <p>{`Here's the problem: There's no good pre-built React component for this. Or I suck at finding it.`}</p>
    <p>{`There's a `}<a parentName="p" {...{
        "href": "https://github.com/jasonslyvia/react-lazyload"
      }}>{`react-lazyload`}</a>{`, which does the lazy loading part for you. It keeps components from mounting into the DOM until the user actually sees them.`}</p>
    <p>{`This helps make your website faster to load. No need to load large images until a user can actually see them.`}</p>
    <p>{`But the default experience can be jarring. When images finally load, they just pop into view.`}</p>
    <p><inlineCode parentName="p">{`react-lazyload`}</inlineCode>{` offers a fade-in example, but it's outdated and doesn't work with modern libraries. And it's cumbersome to use.`}</p>
    <p>{`So I built a general `}<inlineCode parentName="p">{`FadeIn`}</inlineCode>{` component. I'll opensource it, but it needs some polish first, and I'd like your opinions on how to make it better.`}</p>
    <h2 {...{
      "id": ""
    }}></h2>
    <p>{`The component is just 40 lines of code. Pretty simple.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class FadeIn extends React.Component {
  state = {
    loaded: false,
  };
  onLoad = () => this.setState({ loaded: true });

  render() {
    const { height, children } = this.props,
      { loaded } = this.state;

    return (
      <lazyload height={height} offset={150}>
        <transition in={loaded} timeout={duration}>
          {(state) => (
            <div style={{ ...defaultstyle, ...transitionstyles[state] }}>
              {children(this.onLoad)}
            </div>
          )}
        </transition>
      </lazyload>
    );
  }
}
FadeIn.propTypes = {
  height: PropTypes.number,
  children: PropTypes.func,
};
`}</code></pre>
    <p><inlineCode parentName="p">{`FadeIn`}</inlineCode>{` keeps local `}<inlineCode parentName="p">{`loaded`}</inlineCode>{` state to keep track of what to show. `}<inlineCode parentName="p">{`false`}</inlineCode>{` means "stay transparent", `}<inlineCode parentName="p">{`true`}</inlineCode>{` means "fade to full opacity".`}</p>
    <p>{`It uses react-lazyload's `}<inlineCode parentName="p">{`LazyLoad`}</inlineCode>{` component to handle the lazy loading part, and `}<inlineCode parentName="p">{`Transition`}</inlineCode>{` from `}<a parentName="p" {...{
        "href": "https://github.com/reactjs/react-transition-group"
      }}>{`react-transition-group`}</a>{` to drive the CSS transition for fading in. This is the part that's changed a lot since the `}<a parentName="p" {...{
        "href": "https://github.com/jasonslyvia/react-lazyload/blob/master/examples/pages/fadein.js"
      }}>{`official fadein lazyload example`}</a>{`.`}</p>
    <p>{`Using the children render function approach, you can ask `}<inlineCode parentName="p">{`FadeIn`}</inlineCode>{` to render anything you want. It gets wrapped in a div that handles the fade effect.`}</p>
    <p>{`All you have to do is trigger the `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` callback once your content is ready. When your image is done loading for example.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<fadein height={400}>
{onLoad =>
  <img class={cx('img')} src={subheader2} onload={onLoad}>
}
</fadein>
`}</code></pre>
    <p>{`You render `}<inlineCode parentName="p">{`<FadeIn>`}</inlineCode>{`, give it a height so things don't jump around, and pass a children function that takes an `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` callback. When you're ready to trigger the transition, you call `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` and `}<inlineCode parentName="p">{`FadeIn`}</inlineCode>{` does its thing.`}</p>
    <p>{`Did you know `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` was a built-in DOM callback that all browsers support? I had no idea.`}</p>
    <p><inlineCode parentName="p">{`onLoad`}</inlineCode>{` is important because without it the FadeIn transition might end before the image has even come down the pipe. That's the issue I was facing at first `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=k5DnlFjuf80"
      }}>{`when I livecoded this`}</a>{`.`}</p>
    <h2 {...{
      "id": "recommendations"
    }}>{`Recommendations?`}</h2>
    <p>{`I want to opensource this. I know it's simple, but it could save people like an hour of their time. That's worth it, right?`}</p>
    <p>{`What else does it need to do? What should I name it? Would you use it?`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/swizec"
      }}>{`Shout at me on Twitter.`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      